import { gsap } from 'gsap';

import { getAnimationSystem, initializeAnimationSystem } from './components-frontend/UnifiedAnimationSystem.js';
import { BasicAnimationsModule } from './components-frontend/modules/BasicAnimationsModule.js';
import { MouseDistanceModule } from './components-frontend/modules/MouseDistanceModule.js';
import { TextAnimationsModule } from './components-frontend/modules/TextAnimationsModule.js';
import { ParallaxModule } from './components-frontend/modules/ParallaxModule.js';

import { 
    initIntersectionTrigger,
    initScrollTrigger,
    initHoverTrigger,
    initClickTrigger,
    initAdvancedScrollTrigger
} from './components-frontend/AdvancedTriggers.js';

import { ScrollProgressCalculator } from './components-frontend/ScrollProgressCalculator.js';
import { touchState } from './components-frontend/methods.js';

import './style.scss';

if (typeof window !== 'undefined') {
    window.ScrollProgressCalculator = ScrollProgressCalculator;
}

let animationSystem = null;

function initializeUnifiedSystem() {
    if (animationSystem) return animationSystem;
    
    animationSystem = getAnimationSystem();
    
    animationSystem.registerModule('basicAnimations', new BasicAnimationsModule());
    animationSystem.registerModule('mouseDistance', new MouseDistanceModule());
    animationSystem.registerModule('textAnimations', new TextAnimationsModule());
    animationSystem.registerModule('parallax', new ParallaxModule());
    
    window.animationSystem = animationSystem;
    
    return animationSystem;
}

function canUseUnifiedSystem(triggerType, animationData) {
    const supportedTriggers = ['intersection', 'scroll', 'hover', 'click', 'mousePosition'];
    return supportedTriggers.includes(triggerType);
}

function setupAnimationAreaScrub(block, animationData, elementId) {
    const scrollSettings = animationData.eventsConfig.triggerSettings.scroll;
    
    if (!scrollSettings || !scrollSettings.useAnimationArea) {
        return null;
    }
    
    const progressCalculator = new ScrollProgressCalculator(block, {
        animationStart: scrollSettings.animationStart || 0,
        animationEnd: scrollSettings.animationEnd || 100,
        scrollDirection: scrollSettings.scrollDirection || 'vertical'
    });
    
    const elementData = animationSystem.elements.get(block);
    if (!elementData) return null;
    
    const basicEffect = elementData.activeEffects.get('basicAnimations');
    if (!basicEffect?.module) return null;

    const initialProps = basicEffect.module.convertPropsForGSAP(basicEffect.state.keyframes[0].props);
    gsap.set(block, initialProps);
    
    progressCalculator.onProgress((progress) => {
        if (basicEffect.module.setProgress) {
            basicEffect.module.setProgress(basicEffect.state, progress);
        }
    });
    
    progressCalculator.onEnter(() => {
        block.classList.add('in-animation-area');
    });
    
    progressCalculator.onLeave(() => {
        block.classList.remove('in-animation-area');
    });
    
    return progressCalculator;
}

function animateElementToFinal(elementData) {
    const basicEffect = elementData.activeEffects.get('basicAnimations');
    if (basicEffect?.module?.animateToFinal) {
        basicEffect.module.animateToFinal(elementData.element, basicEffect.state);
    }
    
    const textEffect = elementData.activeEffects.get('textAnimations');
    if (textEffect?.module?.executeAnimation && textEffect.state?.enabled) {
        textEffect.module.executeAnimation(textEffect.state);
    }
}

function animateElementToInitial(elementData) {
    const basicEffect = elementData.activeEffects.get('basicAnimations');
    if (basicEffect?.module?.animateToInitial) {
        basicEffect.module.animateToInitial(elementData.element, basicEffect.state);
    }
    
    const textEffect = elementData.activeEffects.get('textAnimations');
    if (textEffect?.state?.enabled) {
        if (textEffect.module?.setProgress) {
            textEffect.module.setProgress(textEffect.state, 0);
        } else if (textEffect.module?.reset) {
            textEffect.module.reset(textEffect.state);
        }
    }
}

function setupScrollDrivenWrapperAnimation(block, animationData) {
    const wrapper = block._scrollDrivenWrapper;
    const range = block._scrollDrivenRange;
    
    if (!wrapper || !range) return null;
    
    // Register element with animation system
    const elementId = animationSystem.registerElement(block, animationData);
    const elementData = animationSystem.elements.get(block);
    if (!elementData) return null;
    
    const basicEffect = elementData.activeEffects.get('basicAnimations');
    if (!basicEffect?.module) return null;
    
    // Set initial state
    const initialProps = basicEffect.module.convertPropsForGSAP(basicEffect.state.keyframes[0].props);
    gsap.set(block, initialProps);
    
    // Function to calculate progress based on wrapper progress and range
    const calculateProgress = (wrapperProgress) => {
        const startPercent = range.start / 100;
        const endPercent = range.end / 100;
        const rangeSize = endPercent - startPercent;
        
        // Oblicz progress w podstawowym zakresie
        let progress = 0;
        if (wrapperProgress <= startPercent) {
            progress = 0;
        } else if (wrapperProgress >= endPercent) {
            progress = 1;
        } else {
            progress = (wrapperProgress - startPercent) / rangeSize;
        }
        
        // Przyspieszenie animacji na podstawie proporcji wysokości KONKRETNEGO STICKY ELEMENTU do viewport
        const viewportHeight = window.innerHeight;

        // Znajdź najbliższy parent motion-scroll-driven-view dla tego motion blocka
        const stickyElement = block.closest('.wp-block-arcanov-motion-scroll-driven-view');
        const stickyHeight = stickyElement ? stickyElement.offsetHeight : wrapper.offsetHeight;
        
        if (stickyHeight > viewportHeight) {
            const heightRatio = stickyHeight / viewportHeight;
            
            // Dostosowywalne przyspieszenie (1.0 = pełna proporcja, 0.5 = połowa, itp.)
            const accelerationFactor = 1.0; // Można zmienić na 0.7-0.8 dla łagodniejszego efektu
            const speedMultiplier = 1 + (heightRatio - 1) * accelerationFactor;
            
            // Przyspieszamy progress proporcjonalnie
            const originalProgress = progress;
            progress = Math.min(1, progress * speedMultiplier);
            
            // Debug info (usuń w produkcji)
            // if (progress !== originalProgress && progress > 0 && progress < 1) {
            //     console.log(`⚡ Height-adjusted progress: ${(originalProgress * 100).toFixed(1)}% → ${(progress * 100).toFixed(1)}%`);
            //     console.log(`   Motion block parent view: ${stickyHeight}px (${heightRatio.toFixed(2)}x viewport)`);
            //     console.log(`   Wrapper: ${wrapper.offsetHeight}px, Parent view:`, stickyElement?.className || 'fallback');
            // }
        }
        
        return progress;
    };
    
    // Function to update animation based on progress
    const updateAnimation = (wrapperProgress) => {
        const progress = calculateProgress(wrapperProgress);
        
        if (basicEffect.module.setProgress) {
            basicEffect.module.setProgress(basicEffect.state, progress);
        }
        
        // Handle text animations if enabled
        const textEffect = elementData.activeEffects.get('textAnimations');
        if (textEffect?.module?.setProgress && textEffect.state?.enabled) {
            textEffect.module.setProgress(textEffect.state, progress);
        }
    };
    
    // Listen for scroll progress events from wrapper
    const handleScrollProgress = (event) => {
        if (event.detail && typeof event.detail.progress === 'number') {
            updateAnimation(event.detail.progress);
        }
    };
    
    wrapper.addEventListener('scrollProgress', handleScrollProgress);
    
    // Re-calculate height ratio on window resize
    let resizeTimeout;
    const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            // Force recalculation by triggering current progress update
            const currentProgress = parseFloat(wrapper.getAttribute('data-scroll-progress')) || 0;
            updateAnimation(currentProgress);
        }, 150);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Initial update with current progress
    const currentProgress = parseFloat(wrapper.getAttribute('data-scroll-progress')) || 0;
    updateAnimation(currentProgress);
    
    // Store cleanup function
    block._scrollDrivenCleanup = () => {
        wrapper.removeEventListener('scrollProgress', handleScrollProgress);
        window.removeEventListener('resize', handleResize);
    };
    
    return elementId;
}

const initializeBlockAnimations = function(block) {
    if (!block || block._offAnimationInitialized) {
        return null;
    }
    
    let animationData;
    try {
        const rawData = block.dataset.gsapAnimation || '{}';
        animationData = JSON.parse(rawData)
    } catch (error) {
        console.error(`âŒ Error parsing animation data for ${block.id}:`, error);
        return null;
    }
    
    if (!animationData || animationData.enabled === false) {
        return null;
    }
    
    // Check if nested in scroll-driven wrapper
    const scrollDrivenWrapper = block.closest('.arcanov-motion-block-scroll-driven-wrapper');
    if (scrollDrivenWrapper) {
        block._scrollDrivenWrapper = scrollDrivenWrapper;
        block._scrollDrivenRange = animationData.scrollDrivenRange || { start: 0, end: 100 };
    }
    
    block._offAnimationInitialized = true;
    
    if (!animationSystem) {
        initializeUnifiedSystem();
    }
    
    const triggerType = animationData.eventsConfig.triggerType;
    const canUseUnified = canUseUnifiedSystem(triggerType, animationData);

    // Handle scroll-driven wrapper case
    if (block._scrollDrivenWrapper && animationData.eventsConfig.activeScrollDriven) {
        return setupScrollDrivenWrapperAnimation(block, animationData);
    }
    
    if (canUseUnified) {
        const elementId = animationSystem.registerElement(block, animationData);
        
        if (triggerType === 'scroll' && animationData.eventsConfig.triggerSettings.scroll.useAnimationArea) {
            const progressCalculator = setupAnimationAreaScrub(block, animationData, elementId);
            if (progressCalculator) {
                block._progressCalculator = progressCalculator;
            }
        } else {
            setupDiscreteTrigger(block, triggerType, animationData, elementId);
        }
        
        return elementId;
    }
    
    return null;
};

function setupDiscreteTrigger(block, triggerType, animationData, elementId) {
    const triggerConfig = animationData.eventsConfig;

    const animationConfig = {
        duration: animationData.basicAnimations.timing.duration,
        delay: animationData.basicAnimations.timing.delay,
        ease: animationData.basicAnimations.timing.ease,
        once: triggerType === 'intersection' 
            ? (triggerConfig.triggerSettings[triggerType]?.once !== false)
            : false,
        
        customAnimationFunction: () => {
            const elementData = animationSystem.elements.get(block);
            if (elementData) {
                animateElementToFinal(elementData);
            }
            return gsap.timeline();
        },
        
        customReverseAnimationFunction: () => {
            const elementData = animationSystem.elements.get(block);
            if (elementData) {
                animateElementToInitial(elementData);
            }
            return gsap.timeline();
        }
    };
    
    let triggerEffect = null;
    
    switch (triggerType) {
        case 'intersection':
            triggerEffect = initIntersectionTrigger(block, triggerConfig.triggerSettings.intersection, animationConfig);
            break;
        case 'scroll':
            triggerEffect = initScrollTrigger(block, triggerConfig.triggerSettings.scroll, animationConfig);
            break;
        case 'hover':
            triggerEffect = initHoverTrigger(block, triggerConfig.triggerSettings.hover, animationConfig);
            break;
        case 'click':
            triggerEffect = initClickTrigger(block, triggerConfig.triggerSettings.click, animationConfig);
            break;
        case 'mousePosition':
            break;
    }
    
    if (triggerEffect) {
        block._discreteTriggerEffect = triggerEffect;
    }
}

/**
 * funkcja initAnimations z trackowaniem
 */
const initAnimations = function() {
    initializeUnifiedSystem();
    
    const animationBlocks = document.querySelectorAll('.off-animation-block');
    
    let successCount = 0;
    let errorCount = 0;
    let mobileCount = 0;
    let scrollAreaCount = 0;
    
    animationBlocks.forEach(block => {
        try {
            const elementId = initializeBlockAnimations(block);
            
            if (elementId) {
                successCount++;
                
                if (touchState.isMobile) {
                    mobileCount++;
                }
                
                if (block._progressCalculator) {
                    scrollAreaCount++;
                }
            }
        } catch (error) {
            console.error(`âŒ Error initializing block ${block.id}:`, error);
            errorCount++;
        }
    });
};

/**
 * MutationObserver dla dynamicznych blokÃ³w
 */
if (typeof MutationObserver !== 'undefined') {
    const globalObserver = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            if (mutation.type === 'childList') {
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType === 1) {
                        let blocksToInit = [];
                        
                        if (node.classList && node.classList.contains('off-animation-block')) {
                            blocksToInit.push(node);
                        }
                        
                        if (node.querySelectorAll) {
                            const nestedBlocks = node.querySelectorAll('.off-animation-block');
                            blocksToInit.push(...nestedBlocks);
                        }
                        
                        blocksToInit.forEach(function(block) {
                            try {
                                initializeBlockAnimations(block);
                            } catch (error) {
                                console.error(`âŒ Error initializing dynamic block ${block.id}:`, error);
                            }
                        });
                    }
                });
            }
        });
    });
    
    globalObserver.observe(document.body, {
        childList: true,
        subtree: true
    });
}

// /**
//  * DOMContentLoaded
//  */
// document.addEventListener('DOMContentLoaded', () => {
//     initAnimations();
// });
// init once for barba. Observer will do the rest
if ( !window.arcanovMotionFrontendInitialized ) {
    window.arcanovMotionFrontendInitialized = true;
    initAnimations();
}

/**
 * Global API
 */
window.offScrollAnimations = {
    system: () => animationSystem,
    
    reinitialize: function() {
        if (animationSystem) {
            animationSystem.destroy();
            animationSystem = null;
        }
        
        document.querySelectorAll('.off-animation-block').forEach(block => {
            block._offAnimationInitialized = false;
            if (block._progressCalculator) {
                block._progressCalculator.destroy();
                delete block._progressCalculator;
            }
            if (block._scrollDrivenCleanup) {
                block._scrollDrivenCleanup();
                delete block._scrollDrivenCleanup;
                delete block._scrollDrivenWrapper;
                delete block._scrollDrivenRange;
            }
        });
        
        initAnimations();
    },
    
    getStats: function() {
        if (animationSystem) {
            const baseStats = animationSystem.getStats();
            
            let mobileStats = { mobileElements: 0, fallbackTypes: {}, deviceInfo: null };
            let scrollAreaStats = { scrollAreaElements: 0, discreteElements: 0 };
            
            animationSystem.elements.forEach((elementData) => {
                const mouseEffect = elementData.activeEffects.get('mouseDistance');
                if (mouseEffect?.state?.isMobileFallback) {
                    mobileStats.mobileElements++;
                    const fallbackType = mouseEffect.state.fallbackType;
                    mobileStats.fallbackTypes[fallbackType] = (mobileStats.fallbackTypes[fallbackType] || 0) + 1;
                    
                    if (!mobileStats.deviceInfo && mouseEffect.state.deviceInfo) {
                        mobileStats.deviceInfo = mouseEffect.state.deviceInfo;
                    }
                }
                
                if (elementData.element._progressCalculator) {
                    const scrollSettings = elementData.config?.eventsConfig?.triggerSettings?.scroll;
                    if (scrollSettings?.useAnimationArea) {
                        scrollAreaStats.scrollAreaElements++;
                    } else {
                        scrollAreaStats.discreteElements++;
                    }
                }
            });
            
            return { ...baseStats, mobile: mobileStats, scrollArea: scrollAreaStats };
        }
        return { error: 'System not initialized' };
    }
};